import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Alert,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { AuthContext } from '../../authContext/context';
import { postRequest, getRequest, putRequest } from '../../lib/api';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useForm, Controller } from 'react-hook-form';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => c._id === customerId);
  };

  const handleCreateCustomer = async () => {
    // Check if at least email or phone is provided
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    try {
      setCreating(true);
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || '',
        dob: '',
        address1: '',
        address2: '',
        town: '',
        country: '',
        postcode: ''
      };

      const res = await postRequest('private/createCustomer', payload, authToken);

      if (res?.status === 200 && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Customer created successfully!',
          timer: 1500,
          showConfirmButton: false
        });

        // Add the newly created customer
        onCustomerAdd(res.data);

        // Reset search fields
        setSearchFields({
          title: '',
          forename: '',
          surname: '',
          email: '',
          phone_number: ''
        });

        // Close modal
        onHide();
      } else {
        throw new Error(res?.message || 'Failed to create customer');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to create customer'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Purchaser</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCustomerAdded(customer._id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          {isCustomerAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                          No customers found
                        </Typography>
                        {(searchFields.email || searchFields.phone_number || searchFields.forename || searchFields.surname) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'Add Customer'}
                          </Button>
                        )}
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default function PurchaseDetails() {
  const navigate = useNavigate();
  const { id } = useParams(); // This will get the lead_id from the route
  const location = useLocation();
  const authCtx = useContext(AuthContext);

  // Get customerId from URL query params (check both customerId and customer_id)
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get('customerId') || searchParams.get('customer_id');

  // Get basic form data from navigation state (for new leads)
  const basicFormData = location.state?.basicFormData || null;

  const [leadData, setLeadData] = useState(null);
  const [mainLeadData, setMainLeadData] = useState(null); // Store main lead data for creating ConveyancingBranchLead
  const [purchasers, setPurchasers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autoFilling, setAutoFilling] = useState(false);
  const [hasExistingConveyancingLead, setHasExistingConveyancingLead] = useState(false);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [partners, setPartners] = useState([]);

  // Calculate default completion date (today + 3 months)
  const getDefaultCompletionDate = () => {
    const today = new Date();
    today.setMonth(today.getMonth() + 3);
    return today.toISOString().split('T')[0];
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm({
    defaultValues: {
      // Property Details
      line1: '',
      line2: '',
      town: '',
      county: '',
      postcode: '',
      purchasePrice: '',
      ownershipType: 'freehold',
      completionDate: getDefaultCompletionDate(),
      lender_panel_id: '',
      partner_id: '',

      // Info section
      firstTimeBuyer: false,
      helpToBuyISA: false,
      helpToBuy: false,
      helpToBuyEquityLoan: false,
      rightToBuy: false,
      sharedOwnership: false,
      buyingWithMortgage: false,
      newBuild: false,
      islamicMortgage: false,
      auctionRepossession: false,
      giftedDeposit: '',
      multipleProperties: false,
      buyToLet: false,
      arrangingIndemnityPolicy: false,
      unregisteredProperty: false,
      staircasing: false,
      isNonUKResident: false,
      nonResidentialMixed: false
    }
  });

  // Postcode branch validation states
  const [postcodeMatch, setPostcodeMatch] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [checkingPostcode, setCheckingPostcode] = useState(false);
  const [isPostcodeValid, setIsPostcodeValid] = useState(false); // Track if postcode is approved

  // Watch postcode field
  const postcodeValue = watch('postcode');

  // Ownership type options
  const ownershipTypes = [
    { value: 'freehold', label: 'Freehold' },
    { value: 'leasehold', label: 'Leasehold' }
  ];

  // Check postcode in branches when postcode changes
  useEffect(() => {
    const checkPostcode = async () => {
      if (!postcodeValue || postcodeValue.trim().length < 3) {
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);
        return;
      }

      try {
        setCheckingPostcode(true);

        // Clear previous states before starting validation
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);

        // STEP 1: Check if postcode exists via checkPostcodeInBranch API
        console.log('🔍 Step 1: Checking if postcode exists');
        const checkRes = await postRequest(
          'private/checkPostcodeInBranch',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 1 Result:', checkRes);

        // Check if Step 1 failed (invalid postcode)
        if (checkRes?.status === 406 || checkRes?.data?.exists === false) {
          // ❌ Step 1 Failed: Invalid postcode (not in UK postcode database)
          console.log('❌ Step 1 Failed: Postcode does not exist');
          setPostcodeMatch(null);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(false);

          // Show error toast with API message
          Swal.fire({
            title: 'Invalid Postcode',
            text: checkRes?.message || 'This postcode does not exist in the UK postcode database. Please enter a valid UK postcode.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
          return; // Stop here, don't proceed to Step 2
        }

        // ✅ Step 1 Passed: Postcode exists
        console.log('✅ Step 1 Passed: Postcode exists');
        console.log('🔍 Step 2: Matching branch by postcode prefix');

        // STEP 2: Match branch by postcode prefix (only runs if Step 1 passed)
        const matchRes = await postRequest(
          'private/matchBranchByPostcode',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 2 Result:', matchRes);

        if (matchRes?.data?.matched) {
          // ✅ Step 2 Passed: Branch matched
          console.log('✅ Step 2 Passed: Branch matched');
          setPostcodeMatch(matchRes.data.branch);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(true);
        } else {
          // ❌ Step 2 Failed: Valid postcode but no branch serves this area
          console.log('❌ Step 2 Failed: No branch serves this postcode area');
          setPostcodeMatch(null);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(false);

          Swal.fire({
            title: 'No Branch Coverage',
            text: 'This postcode area is not currently served by any of our branches. Please contact support.',
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
        }
      } catch (error) {
        console.error('Error checking postcode:', error);
        setIsPostcodeValid(false);
      } finally {
        setCheckingPostcode(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(checkPostcode, 500);
    return () => clearTimeout(timeoutId);
  }, [postcodeValue, authCtx.token]);

  // Fetch active lender panels
  const fetchActiveLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      console.log('📋 Lender Panels Response:', response);
      if (response?.status === 200 && response?.data) {
        console.log('✅ Lender Panels fetched:', response.data);
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      console.log('📋 Partners Response:', response);
      if (response?.status === 200 && response?.data?.docs) {
        console.log('✅ Partners fetched:', response.data.docs);
        setPartners(response.data.docs);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Fetch conveyancing lead details
  const fetchLeadDetails = async () => {
    console.log('=== FETCH LEAD DETAILS START ===');
    console.log('📍 Fetching lead with ID:', id);

    // Basic validation
    if (!id) {
      console.error('❌ No ID provided');
      setLoading(false);
      return;
    }

    // If id is 'new', skip fetching - this is a brand new lead
    if (id === 'new') {
      console.log('📝 New lead creation mode - skipping fetch');
      setHasExistingConveyancingLead(false);
      setLoading(false);
      // Don't set purchasers here - let the fetchInitialCustomer useEffect handle it from URL
      return;
    }

    try {
      setLoading(true);

      // Use the dedicated endpoint for getting edit data
      const endpoint = `private/getEditDataPurchaseConveyancing/${id}`;
      console.log('🌐 Using API Endpoint:', endpoint);
      console.log('🔍 Looking for lead with ID:', id);

      const response = await getRequest(endpoint, authCtx.token);
      console.log('📡 API Response received:', response);
      console.log('📡 Response status:', response?.status, 'Type:', typeof response?.status);
      console.log('📡 Is 404?', response?.status === 404, 'or', response?.status == 404);

      // Check if it's a 404 - expected for new conveyancing leads
      if (response?.status === 404 || response?.status == '404') {
        console.log('📝 No existing conveyancing data found - ready for initial creation');
        setHasExistingConveyancingLead(false);

        // Try to fetch the main lead data to get group_code, product_id, etc.
        try {
          console.log('📊 Attempting to fetch main lead data for ID:', id);
          const mainLeadResponse = await postRequest(`private/get-single-lead/${id}`, {}, authCtx.token);

          console.log('📡 Main Lead Response (no 404 case):', mainLeadResponse);
          console.log('📡 Response status:', mainLeadResponse?.status);

          if ((mainLeadResponse?.status === 200 || mainLeadResponse?.status === 'success') && mainLeadResponse?.data) {
            console.log('✅ Found main lead data:', mainLeadResponse.data);
            console.log('📋 Main lead fields:', {
              lead_id: mainLeadResponse.data.lead_id,
              group_code: mainLeadResponse.data.group_code,
              product_id: mainLeadResponse.data.product_id,
              created_id: mainLeadResponse.data.created_id,
              customers: mainLeadResponse.data.customers?.length || 0,
              acquisition: mainLeadResponse.data.acquisition
            });
            console.log('🔍 Has customer_ids?', !!mainLeadResponse.data.customer_ids);
            console.log('🔍 customer_ids:', mainLeadResponse.data.customer_ids);
            console.log('🔍 Has customers?', !!mainLeadResponse.data.customers);
            console.log('🔍 customers:', mainLeadResponse.data.customers);

            setMainLeadData(mainLeadResponse.data);

            // Set customers from main lead - handle both customer_ids and customers field names
            if (mainLeadResponse.data.customer_ids && Array.isArray(mainLeadResponse.data.customer_ids)) {
              console.log('👥 Setting purchasers from customer_ids:', mainLeadResponse.data.customer_ids.length);
              console.log('👥 Customer IDs array:', mainLeadResponse.data.customer_ids);
              setPurchasers(mainLeadResponse.data.customer_ids);
            } else if (mainLeadResponse.data.customers && Array.isArray(mainLeadResponse.data.customers)) {
              console.log('👥 Setting purchasers from customers:', mainLeadResponse.data.customers.length);
              console.log('👥 Customers array:', mainLeadResponse.data.customers);
              setPurchasers(mainLeadResponse.data.customers);
            } else {
              console.log('⚠️ No customer_ids or customers found in main lead response');
            }
          } else {
            console.log('❌ Main lead response failed or has no data');
            console.log('   Status:', mainLeadResponse?.status);
          }
        } catch (leadError) {
          console.log('⚠️ Could not fetch main lead data:', leadError);
        }

        setLoading(false);
        setAutoFilling(false);
        return; // Exit early, form will be empty for creation
      }
      
      if (response?.status === 200 && response?.data) {
        const leadData = response.data;
        console.log('✅ Lead data received successfully!');
        console.log('📊 Full lead data:', JSON.stringify(leadData, null, 2));
        
        setLeadData(leadData);
        setHasExistingConveyancingLead(true);

        console.log('📋 Available fields in lead data:', Object.keys(leadData));
        console.log('🔍 Specific field values:');
        console.log('  - property_address:', leadData.property_address);
        console.log('  - property_value:', leadData.property_value);
        console.log('  - completion_date:', leadData.completion_date);
        console.log('  - ownership_type:', leadData.ownership_type);
        console.log('  - leasehold:', leadData.leasehold);
        console.log('  - first_time_buyer:', leadData.first_time_buyer);
        console.log('  - help_to_buy:', leadData.help_to_buy);
        console.log('  - new_build:', leadData.new_build);
        
        // Auto-fill form data
        if (leadData.property_address) {
          // Handle property address
          if (typeof leadData.property_address === 'string') {
            const parts = leadData.property_address.split(',').map(p => p.trim());
            setValue('line1', parts[0] || '');
            setValue('line2', parts[1] || '');
            setValue('town', parts[2] || '');
            setValue('county', parts[3] || '');
            setValue('postcode', parts[parts.length - 1] || '');
          } else if (typeof leadData.property_address === 'object') {
            setValue('line1', leadData.property_address.address1 || '');
            setValue('line2', leadData.property_address.address2 || '');
            setValue('town', leadData.property_address.town || leadData.property_address.city || '');
            setValue('county', leadData.property_address.county || leadData.property_address.country || leadData.property_address.state || '');
            setValue('postcode', leadData.property_address.postcode || leadData.property_address.postal_code || '');
          }
        }
        
        // Auto-fill other fields
        const price = leadData.property_value || leadData.net_value || leadData.purchase_price || '';
        console.log('💰 Setting purchase price:', price);
        setValue('purchasePrice', String(price));
        
        // Handle ownership type - could be stored as 'Freehold', 'Leasehold', 'Share of Freehold'
        let ownershipType = 'freehold';
        if (leadData.ownership_type) {
          // Convert from backend format to frontend format
          const backendType = leadData.ownership_type.toLowerCase();
          if (backendType === 'leasehold') {
            ownershipType = 'leasehold';
          } else if (backendType === 'share of freehold' || backendType === 'share_of_freehold') {
            ownershipType = 'share_of_freehold';
          } else {
            ownershipType = 'freehold';
          }
        } else if (leadData.leasehold) {
          ownershipType = 'leasehold';
        }
        setValue('ownershipType', ownershipType);
        console.log('🏗️ Setting ownership type:', ownershipType);
        
        if (leadData.completion_date) {
          try {
            const date = new Date(leadData.completion_date);
            const formattedDate = date.toISOString().split('T')[0];
            console.log('📅 Setting completion date:', formattedDate);
            setValue('completionDate', formattedDate);
          } catch (err) {
            console.log('Date parsing error:', err);
          }
        }

        
        // Auto-fill info section - handle all checkbox fields
        console.log('☑️ Setting info checkboxes...');
        console.log('Lead data fields available:', Object.keys(leadData));
        
        // All fields should use boolean values for consistent button behavior
        setValue('firstTimeBuyer', Boolean(leadData.first_time_buyer));
        setValue('helpToBuyISA', Boolean(leadData.help_to_buy_isa));
        setValue('helpToBuy', Boolean(leadData.help_to_buy));
        setValue('helpToBuyEquityLoan', Boolean(leadData.help_to_buy_equity_loan));
        setValue('rightToBuy', Boolean(leadData.right_to_buy));
        setValue('sharedOwnership', Boolean(leadData.shared_ownership));
        setValue('buyingWithMortgage', Boolean(leadData.mortgage_lender || leadData.buying_with_mortgage));
        setValue('newBuild', Boolean(leadData.new_build));
        setValue('islamicMortgage', Boolean(leadData.islamic_mortgage));
        setValue('auctionRepossession', Boolean(leadData.auction_repossession));
        setValue('multipleProperties', Boolean(leadData.multiple_properties));
        setValue('buyToLet', Boolean(leadData.buy_to_let));
        setValue('arrangingIndemnityPolicy', Boolean(leadData.arranging_indemnity_policy));
        setValue('unregisteredProperty', Boolean(leadData.unregistered_property));
        setValue('staircasing', Boolean(leadData.staircasing));
        setValue('isNonUKResident', Boolean(leadData.non_uk_resident || leadData.is_non_uk_resident));
        setValue('nonResidentialMixed', Boolean(leadData.non_residential_mixed_use));
        
        // Set gifted deposit number (special case with input field)
        if (leadData.gifted_deposit) {
          setValue('giftedDeposit', String(leadData.gifted_deposit));
        } else {
          setValue('giftedDeposit', '');
        }
        
        console.log('✅ All checkboxes set!');

        // Lender Panel
        const lenderPanelId = leadData.lender_panel_id
          ? (typeof leadData.lender_panel_id === 'object' ? leadData.lender_panel_id._id : leadData.lender_panel_id)
          : '';
        console.log('🏦 Lender Panel ID:', lenderPanelId);

        // Partner
        const partnerId = leadData.partner_id
          ? (typeof leadData.partner_id === 'object' ? leadData.partner_id._id : leadData.partner_id)
          : '';
        console.log('🤝 Partner ID:', partnerId);

        // Force form to update with all the values
        const formData = {
          line1: leadData.property_address?.address1 || '',
          line2: leadData.property_address?.address2 || '',
          town: leadData.property_address?.town || '',
          county: leadData.property_address?.country || leadData.property_address?.county || '',
          postcode: leadData.property_address?.postcode || '',
          purchasePrice: String(leadData.property_value || ''),
          ownershipType: ownershipType, // Already processed above
          completionDate: leadData.completion_date ? new Date(leadData.completion_date).toISOString().split('T')[0] : '',
          lender_panel_id: String(lenderPanelId),
          partner_id: String(partnerId),
          firstTimeBuyer: Boolean(leadData.first_time_buyer),
          helpToBuyISA: Boolean(leadData.help_to_buy_isa),
          helpToBuy: Boolean(leadData.help_to_buy),
          helpToBuyEquityLoan: Boolean(leadData.help_to_buy_equity_loan),
          rightToBuy: Boolean(leadData.right_to_buy),
          sharedOwnership: Boolean(leadData.shared_ownership),
          buyingWithMortgage: Boolean(leadData.mortgage_lender || leadData.buying_with_mortgage),
          newBuild: Boolean(leadData.new_build),
          islamicMortgage: Boolean(leadData.islamic_mortgage),
          auctionRepossession: Boolean(leadData.auction_repossession),
          giftedDeposit: leadData.gifted_deposit || '',
          multipleProperties: Boolean(leadData.multiple_properties),
          buyToLet: Boolean(leadData.buy_to_let),
          arrangingIndemnityPolicy: Boolean(leadData.arranging_indemnity_policy),
          unregisteredProperty: Boolean(leadData.unregistered_property),
          staircasing: Boolean(leadData.staircasing),
          isNonUKResident: Boolean(leadData.is_non_uk_resident || leadData.non_uk_resident),
          nonResidentialMixed: Boolean(leadData.non_residential_mixed || leadData.non_residential_mixed_use)
        };
        
        console.log('🔄 Resetting form with data:', formData);
        reset(formData);
        
        // Show auto-filling indicator
        setAutoFilling(true);
        
        // Force a small delay to ensure form updates
        setTimeout(() => {
          // Double-check by setting each field individually
          Object.keys(formData).forEach(key => {
            setValue(key, formData[key]);
          });
          setAutoFilling(false);
          console.log('✅ Auto-fill complete!');
        }, 100);
        
        // Set purchasers if available
        if (leadData.customer_ids && Array.isArray(leadData.customer_ids)) {
          console.log('👥 Setting purchasers from existing conveyancing data:', leadData.customer_ids.length);
          setPurchasers(leadData.customer_ids);
        } else if (leadData.customers && Array.isArray(leadData.customers)) {
          console.log('👥 Setting purchasers from customers field:', leadData.customers.length);
          setPurchasers(leadData.customers);
        }
        
        console.log('✅ Auto-fill completed!');
        console.log('=== AUTO-FILL END ===');
      } else {
        console.log('⚠️ No lead data found for auto-fill');
        console.log('Response status:', response?.status);
        console.log('Response data:', response?.data);
        console.log('=== AUTO-FILL END (NO DATA) ===');
        
        // Don't show error - this is expected for new records
        console.log('📝 Form is ready for initial data entry');
      }
    } catch (error) {
      // Check if it's a 404 error (expected for new records)
      const is404 = error.response?.status === 404 ||
                    error.status === 404 ||
                    error.message?.includes('404') ||
                    error.response?.data?.status === 404;

      if (is404) {
        console.log('📝 No existing conveyancing data found (404 - expected for new purchase details)');
        console.log('📊 Now fetching main lead data for customer info...');

        // This is a 404 - try to fetch main lead data for customers
        try {
          const mainLeadResponse = await postRequest(`private/get-single-lead/${id}`, {}, authCtx.token);

          console.log('📡 Main Lead Response:', mainLeadResponse);
          console.log('📡 Response status:', mainLeadResponse?.status);
          console.log('📡 Response data:', mainLeadResponse?.data);

          if ((mainLeadResponse?.status === 200 || mainLeadResponse?.status === 'success') && mainLeadResponse?.data) {
            console.log('✅ Found main lead data:', mainLeadResponse.data);
            console.log('🔍 Checking for customers...');
            console.log('🔍 Has customer_ids?', !!mainLeadResponse.data.customer_ids);
            console.log('🔍 customer_ids:', mainLeadResponse.data.customer_ids);
            console.log('🔍 Has customers?', !!mainLeadResponse.data.customers);
            console.log('🔍 customers:', mainLeadResponse.data.customers);

            setMainLeadData(mainLeadResponse.data);

            // Set customers from main lead - handle both customer_ids and customers field names
            if (mainLeadResponse.data.customer_ids && Array.isArray(mainLeadResponse.data.customer_ids)) {
              console.log('👥 Setting purchasers from customer_ids:', mainLeadResponse.data.customer_ids.length);
              console.log('👥 Customer IDs array:', mainLeadResponse.data.customer_ids);
              setPurchasers(mainLeadResponse.data.customer_ids);
            } else if (mainLeadResponse.data.customers && Array.isArray(mainLeadResponse.data.customers)) {
              console.log('👥 Setting purchasers from customers:', mainLeadResponse.data.customers.length);
              console.log('👥 Customers array:', mainLeadResponse.data.customers);
              setPurchasers(mainLeadResponse.data.customers);
            } else {
              console.log('⚠️ No customer_ids or customers found in main lead response');
            }
          } else {
            console.log('❌ Main lead response failed or has no data');
            console.log('   Status:', mainLeadResponse?.status);
            console.log('   Data:', mainLeadResponse?.data);
          }
        } catch (leadError) {
          console.error('❌ Could not fetch main lead:', leadError);
        }
      } else {
        // Only log and show error for non-404 errors
        console.error('❌ Error in auto-fill:', error);
        console.log('=== AUTO-FILL END (ERROR) ===');

        Swal.fire({
          icon: 'error',
          title: 'Error Loading Data',
          text: 'Failed to load conveyancing data. Please refresh and try again.'
        });
      }
    } finally {
      setLoading(false);
      setAutoFilling(false);
    }
  };


  useEffect(() => {
    console.log('🚀 PurchaseDetails useEffect triggered');
    console.log('   - id from URL:', id);
    console.log('   - authCtx.token exists:', !!authCtx.token);
    console.log('   - Current URL:', window.location.pathname);

    if (id && authCtx.token) {
      console.log('✅ Calling fetchLeadDetails for auto-fill...');
      console.log('   - Lead ID to fetch:', id);
      fetchLeadDetails();
    } else {
      console.log('❌ Cannot fetch - missing id or token');
      if (!id) console.log('   - Missing ID from URL params');
      if (!authCtx.token) console.log('   - Missing auth token');
    }
  }, [id, authCtx.token]);

  // Fetch initial customer from URL and add to purchasers
  useEffect(() => {
    const fetchInitialCustomer = async () => {
      if (customerIdFromUrl && authCtx.token && purchasers.length === 0) {
        console.log('🔍 Fetching initial customer from URL:', customerIdFromUrl);
        try {
          const customerRes = await postRequest(`private/getCustomerIndividualData/${customerIdFromUrl}`, {}, authCtx.token);
          if (customerRes?.data) {
            console.log('✅ Fetched initial customer:', customerRes.data);
            setPurchasers([customerRes.data]);
          }
        } catch (err) {
          console.error('❌ Error fetching initial customer:', err);
        }
      }
    };

    fetchInitialCustomer();
  }, [customerIdFromUrl, authCtx.token]);

  // Debug: Track purchasers state
  useEffect(() => {
    console.log('============================================');
    console.log('👥 PURCHASERS STATE UPDATED');
    console.log('   - Count:', purchasers.length);
    console.log('   - Array:', purchasers);
    if (purchasers.length > 0) {
      console.log('   - Purchasers details:');
      purchasers.forEach((p, idx) => {
        console.log(`     [${idx}] ${p.title} ${p.forename} ${p.surname} - ${p.email} - ${p.phone_number}`);
      });
    } else {
      console.log('   ⚠️ Purchasers array is EMPTY');
    }
    console.log('============================================');
  }, [purchasers]);
  
  // Debug: Track form values
  useEffect(() => {
    const values = watch();
    console.log('📝 Form values updated:', {
      address: `${values.line1}, ${values.town}, ${values.postcode}`,
      price: values.purchasePrice,
      ownership: values.ownershipType,
      completion: values.completionDate,
      hasData: !!(values.line1 || values.purchasePrice)
    });
  }, [watch()]);

  // Fetch active lender panels on mount
  useEffect(() => {
    fetchActiveLenderPanels();
    fetchPartners();
  }, [authCtx.token]);


  const handleAddPurchaser = (customer) => {
    if (!purchasers.some(p => p._id === customer._id)) {
      setPurchasers([...purchasers, customer]);
    }
  };

  const handleRemovePurchaser = (customerId) => {
    setPurchasers(purchasers.filter(p => p._id !== customerId));
  };


  const onSubmit = async (formData) => {
    console.log('🚀 Form submitted with data:', formData);
    console.log('👥 Current purchasers:', purchasers);
    console.log('📍 Lead ID from URL:', id);
    console.log('📊 Existing lead data:', leadData);
    console.log('🔍 Lender Panel ID from form:', formData.lender_panel_id);
    console.log('🔍 Partner ID from form:', formData.partner_id);

    // Validate we have ID
    if (!id) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No lead ID found. Please check the URL.'
      });
      return;
    }
    
    if (purchasers.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please add at least one purchaser'
      });
      return;
    }

    // Block save until both postcode checks (UK database + branch match) have passed
    if (checkingPostcode) {
      Swal.fire({
        icon: 'info',
        title: 'Please Wait',
        text: 'Postcode verification is still in progress. Please try again in a moment.'
      });
      return;
    }

    if (!isPostcodeValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Postcode',
        text: 'Please enter a valid UK postcode that is covered by one of our branches before saving.'
      });
      return;
    }

    // Determine if we're creating initial purchase details or updating existing ones
    const isInitialCreate = !hasExistingConveyancingLead;
    console.log('🔍 Mode:', isInitialCreate ? 'INITIAL CREATE' : 'UPDATE EXISTING');
    console.log('🔄 Processing conveyancing lead with ID:', id);
    console.log('📊 Has existing conveyancing lead:', hasExistingConveyancingLead);
    console.log('📊 Lead data exists:', !!leadData);
    console.log('📊 Lead data has lead_id:', leadData?.lead_id);
    
    // Prepare update payload
    const updatePayload = {
      // Property fields
      property_address: {
        address1: formData.line1,
        address2: formData.line2,
        town: formData.town,
        country: formData.county, // Backend expects 'country'
        postcode: formData.postcode
      },
      property_value: parseFloat(formData.purchasePrice) || 0,
      ownership_type: formData.ownershipType === 'leasehold' ? 'Leasehold' : 
                      formData.ownershipType === 'share_of_freehold' ? 'Share of Freehold' : 'Freehold',
      completion_date: formData.completionDate,
      
      // Customer fields
      customer_ids: purchasers.map(p => p._id || p),

      // Lender Panel
      lender_panel_id: formData.lender_panel_id || null,
      // Partner
      partner_id: formData.partner_id || null,
      // Manual Branch ID (if branch was selected from dropdown)
      manual_branch_id: selectedBranch || null,

      // ✅ Info object - all checkboxes (STANDARDIZED TO camelCase)
      info: {
        firstTimeBuyer: formData.firstTimeBuyer,
        helpToBuyISA: formData.helpToBuyISA,        // ✅ Match form field name
        helpToBuy: formData.helpToBuy,
        helpToBuyEquityLoan: formData.helpToBuyEquityLoan,
        rightToBuy: formData.rightToBuy,
        sharedOwnership: formData.sharedOwnership,
        buyingWithMortgage: formData.buyingWithMortgage,
        newBuild: formData.newBuild,
        islamicMortgage: formData.islamicMortgage,
        auctionRepossession: formData.auctionRepossession,
        giftedDeposit: formData.giftedDeposit || '',
        multipleProperties: formData.multipleProperties,
        buyToLet: formData.buyToLet,
        arrangingIndemnityPolicy: formData.arrangingIndemnityPolicy,
        unregisteredProperty: formData.unregisteredProperty,
        staircasing: formData.staircasing,
        isNonUKResident: formData.isNonUKResident, // ✅ Match form field name
        nonResidentialMixed: formData.nonResidentialMixed,
      },

      // Additional required fields from main lead
      acquisition: mainLeadData?.acquisition, // Use acquisition from main lead (no default)
      override_date: new Date(), // Current date as override date
      status: 'new', // Default status
      sub_status: 'lead_in' // Default sub_status
    };
    
    // If this is a new lead (id === 'new'), use basicFormData from navigation state
    if (id === 'new' && basicFormData) {
      console.log('📋 New lead mode. Using basic form data from navigation:', basicFormData);

      updatePayload.group_code = basicFormData.group_code;
      updatePayload.product_id = basicFormData.product_id;
      updatePayload.created_id = basicFormData.created_id;
      updatePayload.service_type = basicFormData.service_type || 'Purchase';
      updatePayload.acquisition = basicFormData.acquisition;
      updatePayload.override_date = basicFormData.override_date;

      // Include vendors/purchasers if present
      if (basicFormData.vendors) {
        updatePayload.vendors = basicFormData.vendors;
      }
      if (basicFormData.purchasers) {
        updatePayload.purchasers = basicFormData.purchasers;
      }
    }
    // If this is an initial create and we have main lead data, add required fields
    else if (isInitialCreate) {
      console.log('📋 Initial create mode. Main lead data:', mainLeadData);
      if (mainLeadData) {
        console.log('✅ Adding main lead data to payload for ConveyancingBranchLead creation');
        console.log('   - group_code:', mainLeadData.group_code);
        console.log('   - product_id:', mainLeadData.product_id);
        console.log('   - created_id:', mainLeadData.created_id);

        updatePayload.group_code = mainLeadData.group_code?._id || mainLeadData.group_code;
        updatePayload.product_id = mainLeadData.product_id?._id || mainLeadData.product_id;
        updatePayload.created_id = mainLeadData.created_id?._id || mainLeadData.created_id || localStorage.getItem('id');
        updatePayload.service_type = 'Purchase'; // Default for purchase details form
        updatePayload.branch_id = mainLeadData.branch_id?._id || mainLeadData.branch_id;

        // Use acquisition from main lead if available
        if (mainLeadData.acquisition) {
          updatePayload.acquisition = mainLeadData.acquisition;
        }

        // Use override_date from main lead if available
        if (mainLeadData.override_date) {
          updatePayload.override_date = mainLeadData.override_date;
        }

        // Use status from main lead if available
        if (mainLeadData.status) {
          updatePayload.status = mainLeadData.status;
        }

        // Use sub_status from main lead if available
        if (mainLeadData.sub_status) {
          updatePayload.sub_status = mainLeadData.sub_status;
        }
      } else {
        console.log('⚠️ No main lead data available, trying to get minimal required fields');
        
        // Try to at least get the created_id from localStorage
        const storedCreatedId = localStorage.getItem('id');
        if (storedCreatedId) {
          updatePayload.created_id = storedCreatedId;
        }
        
        // Let's also try to fetch the lead synchronously
        try {
          console.log('🔄 Attempting synchronous fetch of main lead for ID:', id);
          const response = await postRequest(`private/get-single-lead/${id}`, {}, authCtx.token);
          if (response?.status === 200 && response?.data) {
            console.log('✅ Got lead data in submit:', response.data);
            updatePayload.group_code = response.data.group_code?._id || response.data.group_code;
            updatePayload.product_id = response.data.product_id?._id || response.data.product_id;
            updatePayload.created_id = response.data.created_id?._id || response.data.created_id || storedCreatedId;
            updatePayload.service_type = 'Purchase';
            
            // Copy additional fields from the fetched lead
            if (response.data.acquisition) {
              updatePayload.acquisition = response.data.acquisition;
            }
            if (response.data.override_date) {
              updatePayload.override_date = response.data.override_date;
            }
            if (response.data.status) {
              updatePayload.status = response.data.status;
            }
            if (response.data.sub_status) {
              updatePayload.sub_status = response.data.sub_status;
            }
          }
        } catch (error) {
          console.error('❌ Failed to fetch lead in submit:', error);
        }
      }
    }
    
    console.log('📤 Sending payload:', updatePayload);
    
    try {
      let response;
      
      // Always use the new endpoint that saves directly to leads database
      console.log('📝 Saving conveyancing purchase data to main lead...');
      console.log('📌 API Endpoint:', `/api/v1/admin/private/saveConveyancingPurchaseToLead/${id}`);
      console.log('🔑 Token exists:', !!authCtx.token);
      console.log('📦 Payload size:', JSON.stringify(updatePayload).length, 'characters');
      
      response = await postRequest(
        `private/saveConveyancingPurchaseToLead/${id}`,
        updatePayload,
        authCtx.token
      );
      
      console.log('📡 Response:', response);
      console.log('Response status:', response?.status);
      console.log('Response message:', response?.message);
      console.log('Response data:', response?.data);
      
      if (response?.status === 'success' || response?.status === 200) {
        // Get the actual lead_id from response (important for newly created leads)
        const actualLeadId = response?.data?.lead_id || id;

        // 🎯 Call unified API to calculate and save to final_leads_calculations
        try {
          // ⏱️ Wait 300ms for database to commit flags before calculating fees
          console.log('⏳ Waiting 300ms for database to commit flags...');
          await new Promise(resolve => setTimeout(resolve, 300));

          console.log('💰 Calling calculateAndSaveLeadQuote for Purchase...');

          const feeResponse = await postRequest('private/calculateAndSaveLeadQuote', {
            lead_id: actualLeadId,
            service_type: 'Purchase',
            product_id: updatePayload.product_id,
            property_value: updatePayload.property_value,
            ownership_type: updatePayload.ownership_type,
            postcode: formData.postcode,
            partner_id: formData.partner_id || null
          }, authCtx.token);

          console.log('✅ calculateAndSaveLeadQuote response:', feeResponse);
        } catch (feeError) {
          console.error('⚠️ Error calling calculateAndSaveLeadQuote:', feeError);
          // Non-blocking - don't fail the main operation
        }

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: isInitialCreate
            ? 'Purchase details saved successfully!'
            : 'Conveyancing details updated successfully!'
        });

        // Navigate back to the view page with the actual lead_id
        navigate(`/leads/view/conveyancing/${actualLeadId}`);
      } else {
        console.error('❌ Operation failed with response:', response);
        throw new Error(response?.message || 'Operation failed');
      }
    } catch (error) {
      console.error('❌ Error details:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      // Check if it's a network error
      if (error.message?.includes('fetch')) {
        Swal.fire({
          icon: 'error',
          title: 'Network Error',
          text: 'Unable to connect to server. Please ensure the backend is running.'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to save conveyancing details'
        });
      }
    }
  };

  
  // Determine if this is initial creation or update
  const isInitialCreate = !leadData || !leadData.property_value;

  if (loading) {
    return (
      <>
        <BreadcrumbsWrapper
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Leads', path: '/leads' },
            { label: 'Purchase Details', path: '' },
          ]}
        />
        <br />
        <Box sx={{ p: 3 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Loading conveyancing data...
            </Typography>
            <div style={{ marginBottom: '20px' }}>
              Please wait while we fetch the existing information for lead #{id}
            </div>
            <div style={{ fontSize: '24px' }}>⏳</div>
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: !leadData || !leadData.property_value ? 'Add Purchase Details' : 'Edit Conveyancing', path: '' },
        ]}
      />
      <br />

      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
            {!hasExistingConveyancingLead 
              ? `Add Purchase Details - Conveyancing ${id}`
              : `Edit Conveyancing #${leadData?.lead_id || id} - Purchase`}
          </Typography>
          
          {(autoFilling || loading) && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#1976d2' }}>
                🔄 {loading ? 'Loading conveyancing data...' : 'Auto-filling form data...'}
              </Typography>
            </Box>
          )}
          
          {hasExistingConveyancingLead && leadData && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#d4edda', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: '#155724' }}>
                ✅ Editing existing conveyancing lead with auto-filled data
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  console.log('🔄 Manual auto-fill triggered');
                  fetchLeadDetails();
                }}
                sx={{ ml: 2 }}
              >
                Refresh Data
              </Button>
            </Box>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Purchase Details Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Purchase Details
              </Typography>
              
              {/* Property Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Property
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Address</Typography>
                  <Controller
                    name="line1"
                    control={control}
                    rules={{ required: 'Address Line 1 is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 1"
                        variant="outlined"
                        size="small"
                        error={!!errors.line1}
                        helperText={errors.line1?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="line2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 2"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="town"
                    control={control}
                    rules={{ required: 'Town is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Town"
                        variant="outlined"
                        size="small"
                        error={!!errors.town}
                        helperText={errors.town?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="county"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="County"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="postcode"
                    control={control}
                    rules={{ required: 'Postcode is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Postcode"
                        variant="outlined"
                        size="small"
                        error={!!errors.postcode}
                        helperText={errors.postcode?.message}
                      />
                    )}
                  />

                  {/* Postcode checking indicator */}
                  {checkingPostcode && postcodeValue && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      Checking postcode in branches...
                    </Alert>
                  )}

                  {/* Postcode match found */}
                  {postcodeMatch && !checkingPostcode && (
                    <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      ✓ Postcode found in branch: <strong>{postcodeMatch.name}</strong>
                    </Alert>
                  )}

                  {/* Branch selection dropdown when postcode not found */}
                  {!postcodeMatch && availableBranches.length > 0 && !checkingPostcode && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Postcode not found in any branch. Please select a branch:
                      </Alert>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Branch</InputLabel>
                        <Select
                          value={selectedBranch || ''}
                          onChange={(e) => {
                            const branchId = e.target.value;
                            setSelectedBranch(branchId);
                            // When user manually selects a branch, mark postcode as valid
                            if (branchId) {
                              setIsPostcodeValid(true);
                            }

                            // Find the selected branch and auto-fill postcode
                            const branch = availableBranches.find(b => b.id === branchId);
                            if (branch && branch.address.postcode) {
                              setValue('postcode', branch.address.postcode);
                            }
                          }}
                          label="Select Branch"
                        >
                          {availableBranches.map((branch) => (
                            <MenuItem key={branch.id} value={branch.id}>
                              {branch.name} - {branch.address.postcode || 'No postcode'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Grid>
                
                {/* Purchase Price, Ownership Type, Est. Completion Date in one row */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Purchase Price</Typography>
                      <Controller
                        name="purchasePrice"
                        control={control}
                        rules={{ required: 'Purchase price is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            slotProps={{
                              input: {
                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                              }
                            }}
                            error={!!errors.purchasePrice}
                            helperText={errors.purchasePrice?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Ownership Type</Typography>
                      <Controller
                        name="ownershipType"
                        control={control}
                        rules={{ required: 'Ownership type is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth size="small" error={!!errors.ownershipType}>
                            <Select {...field}>
                              <MenuItem value="">Select Ownership Type</MenuItem>
                              {ownershipTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.ownershipType && (
                              <Typography variant="caption" color="error">
                                {errors.ownershipType.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Est. Completion Date</Typography>
                      <Controller
                        name="completionDate"
                        control={control}
                        rules={{ required: 'Completion date is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="date"
                            size="small"
                            slotProps={{
                              inputLabel: {
                                shrink: true,
                              }
                            }}
                            error={!!errors.completionDate}
                            helperText={errors.completionDate?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* Purchasers Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Purchasers
              </Typography>

              <Box sx={{ mb: 3 }}>
                {purchasers.length > 0 ? (
                  purchasers.map((purchaser) => (
                    <Box
                      key={purchaser._id}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 2,
                        p: 1.5,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: '#f9f9f9'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {purchaser.title} {purchaser.forename} {purchaser.surname}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                            📧 {purchaser.email}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                            📞 {purchaser.phone_number}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        onClick={() => handleRemovePurchaser(purchaser._id)}
                        sx={{
                          minWidth: 'auto',
                          color: '#d32f2f',
                          '&:hover': {
                            backgroundColor: '#ffebee'
                          }
                        }}
                      >
                        ✕
                      </Button>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      No purchasers added yet. Click "Add Purchaser" to add customers.
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCustomerModal(true)}
                  sx={{ color: '#6f42c1', mt: 1 }}
                >
                  Add Purchaser
                </Button>
              </Box>

              {/* Lender Panel Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Lender Panel
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="lender_panel_id"
                    control={control}
                    render={({ field }) => {
                      console.log('🔍 Lender Panel Dropdown - field value:', field.value);
                      console.log('🔍 Lender Panel Dropdown - lenderPanels:', lenderPanels);
                      return (
                        <FormControl fullWidth>
                          <Select
                            {...field}
                            displayEmpty
                            sx={{ backgroundColor: 'white' }}
                          >
                            <MenuItem value="">
                              <em>Select Lender Panel (Optional)</em>
                            </MenuItem>
                            {lenderPanels.map((panel) => (
                              <MenuItem key={panel._id} value={panel._id}>
                                {panel.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }}
                  />
                </Grid>
              </Grid>

              {/* Partner Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Partner
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="partner_id"
                    control={control}
                    render={({ field }) => {
                      console.log('🔍 Partner Dropdown - field value:', field.value);
                      console.log('🔍 Partner Dropdown - partners:', partners);
                      return (
                        <FormControl fullWidth>
                          <Select
                            {...field}
                            displayEmpty
                            sx={{ backgroundColor: 'white' }}
                          >
                            <MenuItem value="">
                              <em>Select Partner (Optional)</em>
                            </MenuItem>
                            {partners.map((partner) => (
                              <MenuItem key={partner._id} value={partner._id}>
                                {partner.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Info Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Info
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {[
                  { name: 'firstTimeBuyer', label: 'First Time Buyer' },
                  { name: 'helpToBuyISA', label: 'Help to Buy ISA' },
                  { name: 'helpToBuy', label: 'Help to Buy' },
                  { name: 'helpToBuyEquityLoan', label: 'Help to Buy Equity Loan' },
                  { name: 'rightToBuy', label: 'Right to Buy' },
                  { name: 'sharedOwnership', label: 'Shared Ownership' },
                  { name: 'buyingWithMortgage', label: 'Buying with Mortgage' },
                  { name: 'newBuild', label: 'New Build' },
                  { name: 'islamicMortgage', label: 'Islamic Mortgage' }
                ].map((item) => (
                  <Controller
                    key={item.name}
                    name={item.name}
                    control={control}
                    render={({ field }) => (
                      <Button
                        variant="outlined"
                        onClick={() => field.onChange(!field.value)}
                        sx={{
                          borderColor: field.value ? '#6f42c1' : '#ced4da',
                          backgroundColor: field.value ? '#6f42c1' : 'white',
                          color: field.value ? 'white' : '#495057',
                          textTransform: 'none',
                          fontWeight: 400,
                          fontSize: '0.875rem',
                          px: 2,
                          py: 0.75,
                          '&:hover': {
                            backgroundColor: field.value ? '#5a32a3' : '#f8f9fa',
                            borderColor: field.value ? '#5a32a3' : '#adb5bd'
                          }
                        }}
                      >
                        {item.label}
                      </Button>
                    )}
                  />
                ))}
                
                {/* Additional buttons with special styling */}
                {[
                  { name: 'auctionRepossession', label: 'Auction / Repossession' },
                  { name: 'giftedDeposit', label: 'Gifted Deposit - Num Giftors', hasInput: true },
                  { name: 'multipleProperties', label: 'Multiple Properties' },
                  { name: 'buyToLet', label: 'Buy to Let' },
                  { name: 'arrangingIndemnityPolicy', label: 'Arranging an Indemnity Policy' },
                  { name: 'unregisteredProperty', label: 'Unregistered Property' },
                  { name: 'staircasing', label: 'Staircasing' },
                  { name: 'isNonUKResident', label: 'Is Non-UK Resident' },
                  { name: 'nonResidentialMixed', label: 'Non-Residential / Mixed Use' }
                ].map((item) => (
                  <Box key={item.name} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Controller
                      name={item.name}
                      control={control}
                      render={({ field }) => (
                        <>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              if (item.name === 'giftedDeposit') {
                                field.onChange(field.value ? '' : '1');
                              } else {
                                field.onChange(!field.value);
                              }
                            }}
                            sx={{
                              borderColor: field.value ? '#6f42c1' : '#ced4da',
                              backgroundColor: field.value ? '#6f42c1' : 'white',
                              color: field.value ? 'white' : '#495057',
                              textTransform: 'none',
                              fontWeight: 400,
                              fontSize: '0.875rem',
                              px: 2,
                              py: 0.75,
                              '&:hover': {
                                backgroundColor: field.value ? '#5a32a3' : '#f8f9fa',
                                borderColor: field.value ? '#5a32a3' : '#adb5bd'
                              }
                            }}
                          >
                            {item.label}
                          </Button>
                          {item.hasInput && field.value && (
                            <TextField
                              type="number"
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              size="small"
                              sx={{ 
                                width: '60px', 
                                ml: 1,
                                '& .MuiOutlinedInput-root': {
                                  height: '34px'
                                }
                              }}
                            />
                          )}
                        </>
                      )}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/leads/view/conveyancing/${id}`)}
                sx={{
                  borderColor: '#6c757d',
                  color: '#6c757d',
                  '&:hover': {
                    borderColor: '#5a6268',
                    backgroundColor: 'rgba(108, 117, 125, 0.04)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={checkingPostcode || !isPostcodeValid}
                sx={{
                  backgroundColor: '#6f42c1',
                  '&:hover': {
                    backgroundColor: '#5a32a3'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                {isInitialCreate ? 'Save Purchase Details' : 'Save Changes'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      {/* Customer Search Modal */}
      <CustomerSearchModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleAddPurchaser}
        selectedCustomers={purchasers}
      />
    </>
  );
}